import { useState, useContext } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { MapPin, TrendingUp, Building2, Users, Award, Search, X } from "lucide-react";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

export default function Franchises() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [enquiryDialog, setEnquiryDialog] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [investmentRange, setInvestmentRange] = useState("all");
  const [enquiryForm, setEnquiryForm] = useState({
    name: "",
    email: "",
    mobile: "",
    message: ""
  });

  const franchises = [
    {
      id: 1,
      name: "Fast Food Franchise",
      brand: "QuickBite Express",
      investment: "₹25-30 Lakhs",
      area: "1000-1500 sq ft",
      roi: "25-30%",
      support: "Complete setup & training",
      locations: "Pan India",
      image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=500&q=80",
      category: "Food & Beverage",
      yearEstablished: "2015",
      outlets: "200+"
    },
    {
      id: 2,
      name: "Fitness Center Franchise",
      brand: "FitZone Gyms",
      investment: "₹40-50 Lakhs",
      area: "2000-3000 sq ft",
      roi: "30-35%",
      support: "Equipment & marketing",
      locations: "Tier 1 & 2 Cities",
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=500&q=80",
      category: "Health & Fitness",
      yearEstablished: "2012",
      outlets: "150+"
    },
    {
      id: 3,
      name: "Education Franchise",
      brand: "SmartKids Learning",
      investment: "₹15-20 Lakhs",
      area: "800-1200 sq ft",
      roi: "35-40%",
      support: "Curriculum & training",
      locations: "All Major Cities",
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=500&q=80",
      category: "Education",
      yearEstablished: "2018",
      outlets: "300+"
    },
    {
      id: 4,
      name: "Cafe Franchise",
      brand: "Coffee Culture",
      investment: "₹20-25 Lakhs",
      area: "600-1000 sq ft",
      roi: "28-32%",
      support: "Full setup & branding",
      locations: "Metro Cities",
      image: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=500&q=80",
      category: "Food & Beverage",
      yearEstablished: "2016",
      outlets: "180+"
    },
    {
      id: 5,
      name: "Retail Store Franchise",
      brand: "ShopMart Express",
      investment: "₹30-35 Lakhs",
      area: "1500-2000 sq ft",
      roi: "22-28%",
      support: "Inventory & POS system",
      locations: "Urban Areas",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&q=80",
      category: "Retail",
      yearEstablished: "2014",
      outlets: "250+"
    },
    {
      id: 6,
      name: "Salon & Spa Franchise",
      brand: "Glamour Studio",
      investment: "₹18-22 Lakhs",
      area: "800-1200 sq ft",
      roi: "30-35%",
      support: "Equipment & staff training",
      locations: "All Cities",
      image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=500&q=80",
      category: "Beauty & Wellness",
      yearEstablished: "2017",
      outlets: "120+"
    },
    {
      id: 7,
      name: "Cloud Kitchen Franchise",
      brand: "TastyBites Kitchen",
      investment: "₹12-15 Lakhs",
      area: "400-600 sq ft",
      roi: "35-40%",
      support: "Menu & delivery setup",
      locations: "Metro Cities",
      image: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=500&q=80",
      category: "Food & Beverage",
      yearEstablished: "2020",
      outlets: "90+"
    },
    {
      id: 8,
      name: "Pharmacy Franchise",
      brand: "MediCare Plus",
      investment: "₹25-30 Lakhs",
      area: "600-1000 sq ft",
      roi: "25-30%",
      support: "License & inventory",
      locations: "Pan India",
      image: "https://images.unsplash.com/photo-1576602976047-174e57a47881?w=500&q=80",
      category: "Healthcare",
      yearEstablished: "2013",
      outlets: "400+"
    }
  ];

  // Get unique categories
  const categories = [...new Set(franchises.map(f => f.category))];

  // Investment range options
  const investmentRanges = [
    { value: "all", label: "All Investments" },
    { value: "below-20", label: "Below ₹20 Lakhs" },
    { value: "20-30", label: "₹20-30 Lakhs" },
    { value: "30-50", label: "₹30-50 Lakhs" },
    { value: "above-50", label: "Above ₹50 Lakhs" }
  ];

  // Parse investment string to get minimum value
  const getMinInvestment = (investmentStr) => {
    const match = investmentStr.match(/₹(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  // Filter franchises based on search, category, and investment
  const filteredFranchises = franchises.filter(franchise => {
    // Search filter
    const searchMatch = searchQuery === "" || 
      franchise.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      franchise.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      franchise.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      franchise.locations.toLowerCase().includes(searchQuery.toLowerCase());

    // Category filter
    const categoryMatch = selectedCategory === "all" || franchise.category === selectedCategory;

    // Investment filter
    const minInvestment = getMinInvestment(franchise.investment);
    let investmentMatch = true;
    if (investmentRange === "below-20") {
      investmentMatch = minInvestment < 20;
    } else if (investmentRange === "20-30") {
      investmentMatch = minInvestment >= 20 && minInvestment <= 30;
    } else if (investmentRange === "30-50") {
      investmentMatch = minInvestment > 30 && minInvestment <= 50;
    } else if (investmentRange === "above-50") {
      investmentMatch = minInvestment > 50;
    }

    return searchMatch && categoryMatch && investmentMatch;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setInvestmentRange("all");
  };

  const hasActiveFilters = searchQuery !== "" || selectedCategory !== "all" || investmentRange !== "all";

  const handleFranchiseEnquiry = (franchise) => {
    if (!user) {
      toast.error("Please login to enquire about franchises");
      navigate("/auth");
      return;
    }
    setSelectedFranchise(franchise);
    setEnquiryForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      message: `I am interested in the ${franchise.brand} franchise. Please provide me with more details.`
    });
    setEnquiryDialog(true);
  };

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post('/franchise-enquiries', {
        franchise_id: selectedFranchise.id.toString(),
        franchise_name: selectedFranchise.name,
        franchise_brand: selectedFranchise.brand,
        name: enquiryForm.name,
        email: enquiryForm.email,
        mobile: enquiryForm.mobile,
        message: enquiryForm.message
      });
      toast.success(`Thank you! We'll contact you soon about ${selectedFranchise.brand} franchise.`);
      setEnquiryDialog(false);
      setEnquiryForm({ name: "", email: "", mobile: "", message: "" });
    } catch (error) {
      toast.error('Failed to submit enquiry');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        {/* Header */}
        <div className="text-center mb-12" data-testid="franchise-header">
          <div className="inline-block bg-slate-200 text-slate-700 text-sm font-bold px-4 py-2 rounded-full mb-4">
            FRANCHISE OPPORTUNITIES
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Explore Franchise Opportunities
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Start your entrepreneurial journey with established brands. Browse through verified franchise opportunities across various industries with proven business models.
          </p>
        </div>

        {/* Stats Banner */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-slate-100">
            <Building2 className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <p className="text-3xl font-bold text-slate-900 mb-1">{franchises.length}+</p>
            <p className="text-sm text-slate-600">Franchises Available</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-slate-100">
            <TrendingUp className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <p className="text-3xl font-bold text-slate-900 mb-1">25-40%</p>
            <p className="text-sm text-slate-600">Average ROI</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-slate-100">
            <Users className="w-10 h-10 text-blue-600 mx-auto mb-3" />
            <p className="text-3xl font-bold text-slate-900 mb-1">1500+</p>
            <p className="text-sm text-slate-600">Total Outlets</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-slate-100">
            <Award className="w-10 h-10 text-amber-600 mx-auto mb-3" />
            <p className="text-3xl font-bold text-slate-900 mb-1">100%</p>
            <p className="text-sm text-slate-600">Verified Brands</p>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 mb-8" data-testid="search-filter-section">
          <div className="grid md:grid-cols-4 gap-4">
            {/* Search Input */}
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search franchises by name, brand, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11"
                data-testid="franchise-search-input"
              />
            </div>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="h-11" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Investment Range Filter */}
            <Select value={investmentRange} onValueChange={setInvestmentRange}>
              <SelectTrigger className="h-11" data-testid="investment-filter">
                <SelectValue placeholder="Investment Range" />
              </SelectTrigger>
              <SelectContent>
                {investmentRanges.map(range => (
                  <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Active Filters & Results Count */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-slate-600">
                Showing <span className="font-semibold text-slate-900">{filteredFranchises.length}</span> of {franchises.length} franchises
              </span>
              {hasActiveFilters && (
                <>
                  {searchQuery && (
                    <Badge variant="secondary" className="gap-1">
                      Search: {searchQuery}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchQuery("")} />
                    </Badge>
                  )}
                  {selectedCategory !== "all" && (
                    <Badge variant="secondary" className="gap-1">
                      {selectedCategory}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCategory("all")} />
                    </Badge>
                  )}
                  {investmentRange !== "all" && (
                    <Badge variant="secondary" className="gap-1">
                      {investmentRanges.find(r => r.value === investmentRange)?.label}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setInvestmentRange("all")} />
                    </Badge>
                  )}
                </>
              )}
            </div>
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-slate-600 hover:text-slate-800"
                data-testid="clear-filters-button"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </div>

        {/* Franchise Grid */}
        {filteredFranchises.length === 0 ? (
          <div className="text-center py-16" data-testid="no-results">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Franchises Found</h3>
            <p className="text-slate-600 mb-4">Try adjusting your search or filters</p>
            <Button onClick={clearFilters} variant="outline">
              Clear All Filters
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredFranchises.map((franchise) => (
            <Card key={franchise.id} className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-slate-300" data-testid={`franchise-card-${franchise.id}`}>
              <div className="relative overflow-hidden">
                <img
                  src={franchise.image}
                  alt={franchise.name}
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="text-white font-bold text-lg">{franchise.brand}</h3>
                  <p className="text-white/80 text-sm">{franchise.category}</p>
                </div>
              </div>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Investment</span>
                  <span className="text-sm font-bold text-slate-900">{franchise.investment}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Expected ROI</span>
                  <span className="text-sm font-bold text-emerald-600">{franchise.roi}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Space Required</span>
                  <span className="text-sm font-bold text-slate-900">{franchise.area}</span>
                </div>
                <div className="pt-2">
                  <p className="text-xs text-slate-500 mb-1">Total Outlets</p>
                  <p className="text-sm font-medium text-slate-900">{franchise.outlets}</p>
                </div>
                <div className="pt-2">
                  <p className="text-xs text-slate-500 mb-1">Established</p>
                  <p className="text-sm font-medium text-slate-900">{franchise.yearEstablished}</p>
                </div>
                <Button 
                  onClick={() => handleFranchiseEnquiry(franchise)}
                  className="w-full bg-slate-800 hover:bg-slate-900 mt-4" 
                  size="sm" 
                  data-testid={`enquire-franchise-${franchise.id}`}
                >
                  Enquire Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </div>

      {/* Franchise Enquiry Dialog */}
      <Dialog open={enquiryDialog} onOpenChange={setEnquiryDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl">Franchise Enquiry</DialogTitle>
            <DialogDescription>
              {selectedFranchise && (
                <span className="text-base font-medium text-slate-700">
                  {selectedFranchise.brand} - {selectedFranchise.name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEnquiry} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="enquiry-name">Full Name</Label>
              <Input
                id="enquiry-name"
                value={enquiryForm.name}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                required
                data-testid="enquiry-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-email">Email</Label>
              <Input
                id="enquiry-email"
                type="email"
                value={enquiryForm.email}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                required
                data-testid="enquiry-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-mobile">Mobile Number</Label>
              <Input
                id="enquiry-mobile"
                type="tel"
                value={enquiryForm.mobile}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, mobile: e.target.value })}
                required
                data-testid="enquiry-mobile-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-message">Message</Label>
              <Textarea
                id="enquiry-message"
                rows={4}
                value={enquiryForm.message}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                required
                data-testid="enquiry-message-input"
              />
            </div>
            {selectedFranchise && (
              <div className="bg-slate-100 p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Investment Required:</span>
                  <span className="font-bold text-slate-900">{selectedFranchise.investment}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Expected ROI:</span>
                  <span className="font-bold text-emerald-600">{selectedFranchise.roi}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Space Required:</span>
                  <span className="font-bold text-slate-900">{selectedFranchise.area}</span>
                </div>
              </div>
            )}
            <Button type="submit" className="w-full bg-slate-800 hover:bg-slate-900" data-testid="submit-enquiry-button">
              Submit Enquiry
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
